import { dateFormatters } from '@insurancemenu/imenu-components-lib/dist/helpers';
import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { RateSegment } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import {
  ProductLineSectionType,
  PRODUCT_LINES_CONTENT_MAP,
  isAncillarySection,
  sortByProductLineId,
} from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { Domain, DomainProductLine, RFQForms } from 'types/domainServiceV2';

/**
 * Returns sorted sponsor options from given domains.
 *
 * @param domains - An array of Domain objects.
 * @returns An array of OptionType objects containing sorted sponsor options.
 */
function getSponsorOptions(domains: Domain[]): OptionType[] {
  return (
    domains
      .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
      .map(({ id, name }) => {
        return { value: id, label: name };
      }) || []
  );
}

function filterGeneralPLines(productLineIds: ProductLineId[]): ProductLineId[] {
  return productLineIds.filter(
    (productLine) => PRODUCT_LINES_CONTENT_MAP[productLine]?.section === ProductLineSectionType.GENERAL,
  );
}

function filterAncillaryPLines(productLineIds: ProductLineId[]): ProductLineId[] {
  return productLineIds.filter((productLine) => isAncillarySection(PRODUCT_LINES_CONTENT_MAP[productLine]?.section));
}

/**
 * Finds a domain product line by its ID.
 *
 * @param productLineId - The ID of the product line to find.
 * @param domainProductLines - An array of DomainProductLine objects.
 * @returns The DomainProductLine object if found, otherwise undefined.
 */
function getDomainProductLineById(
  productLineId: ProductLineId,
  domainProductLines: DomainProductLine[],
): DomainProductLine | undefined {
  return domainProductLines.find((productLine) => productLine.productLineId === productLineId);
}

/**
 * Gets the RFQ form ID by key from the domain product line.
 *
 * @param domainProductLine - The DomainProductLine object.
 * @param carrierId - The carrier ID to get the form for.
 * @param formKey - The key of the form in RFQForms.
 * @returns The RFQ form ID as a string.
 */
function getRFQFormByKey(domainProductLine: DomainProductLine, carrierId: string, formKey: keyof RFQForms): string {
  const rfqForms = domainProductLine?.rfqForms;
  // Get the form ID based on the carrier ID or use the default form ID if provided.
  return (rfqForms && (rfqForms[carrierId]?.[formKey] || rfqForms?.def?.[formKey])) || '';
}

/**
 * Filters product line IDs by rate segment from domain product lines.
 *
 * @param domainProductLines - An array of DomainProductLine objects.
 * @param rateSegment - The rate segment to filter by.
 * @returns An array of filtered and sorted ProductLineIds.
 */
function filterPLineIdsByRateSegment(
  domainProductLines: DomainProductLine[],
  rateSegment: RateSegment,
): ProductLineId[] {
  return domainProductLines
    .filter((pLine) => pLine.rateSegment === rateSegment)
    .map((pLine) => pLine.productLineId)
    .sort(sortByProductLineId);
}

function getEffectiveDatesOptions(effectiveDates: string[]): OptionType[] {
  return effectiveDates.map((date) => ({
    value: date,
    label: dateFormatters.normDateToLongAmericanDate(date),
  }));
}

export {
  getSponsorOptions,
  filterGeneralPLines,
  filterAncillaryPLines,
  getDomainProductLineById,
  getRFQFormByKey,
  filterPLineIdsByRateSegment,
  getEffectiveDatesOptions,
};
